import { Box, Modal, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as strings from "common/strings";
import DefaultLoader from "components/common/DefaultLoader";
import InlineSearch from "components/common/InlineSearch";
import { useState } from "react";
import * as staticDataService from "services/staticDataService";
import * as utils from "utils";

const readableAddress = (json) => {
  if (!json || typeof json !== "object") return null;

  if (json.street2 === "-") {
    json.street2 = null;
  }
  if (json.zipCode === "00000") {
    json.zipCode = null;
  }
  if (json.zipCode?.endsWith("-")) {
    json.zipCode = json.zipCode.substr(0, json.zipCode.length - 1);
  }

  const street =
    strings.toSentenceCase(json.street1, " ") +
    (json.street2 ? "\n" + strings.toSentenceCase(json.street2, " ") : "");
  const city = strings.toSentenceCase(json.city, " ");

  if (json.stateOrCountry === json.stateOrCountryDescription) {
    return `${street}\n${city}, ${json.stateOrCountry + " " + json.zipCode}`;
  } else {
    return `${street}\n${city}${json.zipCode ? "\n" + json.zipCode : ""}\n${
      json.stateOrCountryDescription
    }`;
  }
};

const get10KFilingNumber = (json) => {
  if (!json || typeof json !== "object") return null;
  const idx = json.form.findIndex((x) => x === "10-K");
  return idx === -1 ? null : json.accessionNumber[idx];
};

const CompanySearch = ({ open, onClose, onSelect }) => {
  const theme = useTheme();
  const {
    data: tickers,
    isLoading: tickersLoading,
  } = staticDataService.useRetrieveSecTickersQuery();

  const [debounceId, setDebounceId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [searchText, setSearchText] = useState("");
  const hasSearchText = searchText !== "";
  const searchResults = hasSearchText
    ? (tickers ?? [])
        .filter((x) =>
          `${x.t}|${x.n}`
            .toLowerCase()
            .includes(searchText.trim().toLowerCase())
        )
        .splice(0, 50)
    : [];
  const hasMatches = searchResults.length > 0;

  const scrollIntoView = (id, alignToTop = true) => {
    document.getElementById(id).scrollIntoView(alignToTop);
  };

  const activatePrev = () => {
    if (hasSearchText && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
      scrollIntoView("COMPANY_SEARCH_" + activeIndex, false);
    }
  };

  const activateNext = () => {
    if (hasSearchText && activeIndex < searchResults.length - 1) {
      setActiveIndex(activeIndex + 1);
      scrollIntoView("COMPANY_SEARCH_" + activeIndex);
    }
  };

  const handleCompanySelected = (company) => {
    if (company == null || company?.c == null) return;

    const cikCode = `CIK${company.c.toString().padStart(10, "0")}`;
    setIsLoading(true);

    utils
      .plainFetch(`https://data.sec.gov/submissions/${cikCode}.json`)
      .then((response) => response.json())
      .then((json) => {
        setIsLoading(false);
        onSelect({
          name: json.name,
          description: json.sicDescription,
          address: readableAddress(json.addresses?.business),
          cik: company.c,
          accessionNumber: get10KFilingNumber(json.filings?.recent),
        });
      })
      .catch((e) => {
        setIsLoading(false);
        onSelect({
          name: company.n,
          description: null,
          address: null,
          cik: null,
          accessionNumber: null,
        });
      });
  };

  return (
    <Modal open={open} onClose={onClose} BackdropProps={{ invisible: true }}>
      <Box
        sx={{
          maxWidth: "40rem",
          margin: "6rem auto 1rem auto",
          padding: "1rem",
          background: theme.brand.white,
          borderRadius: "0.75rem",
          boxShadow: "0 10px 30px rgba(10,10,10,0.1)",
          border: "1px solid gainsboro",
        }}
      >
        <InlineSearch
          placeholder={"Ticker or company name"}
          autoFocus
          onChange={(text) => {
            if (debounceId != null) clearTimeout(debounceId);
            const id = setTimeout(() => {
              setSearchText(text);
              setActiveIndex(text === "" ? null : 0);
            }, 250);
            setDebounceId(id);
          }}
          onClose={(text) => {
            if (text === "") onClose();
          }}
          onKeyDown={(e) => {
            if (!["Enter", "ArrowUp", "ArrowDown"].includes(e.key)) return;
            e.preventDefault();
            if (e.key === "Enter") {
              handleCompanySelected(
                searchResults.length > 0 && activeIndex != null
                  ? searchResults[activeIndex]
                  : null
              );
            } else if (e.key === "ArrowUp") {
              activatePrev();
            } else if (e.key === "ArrowDown") {
              activateNext();
            }
          }}
          sx={{
            width: "100%",
            maxWidth: "unset",
            marginBottom: hasSearchText && hasMatches ? "0.625rem" : null,
          }}
        />

        {hasSearchText && !hasMatches && (
          <Typography
            variant={"subtitle2"}
            textAlign={"center"}
            paddingTop={1.5}
          >
            {`No matches for '${searchText}'`}
          </Typography>
        )}

        {hasSearchText && hasMatches && !isLoading && (
          <Stack
            sx={{
              display: "inline-flex",
              width: "100%",
              maxHeight: "70vh",
              overflowY: "scroll",
            }}
          >
            {searchResults.map((company, idx) => {
              return (
                <Box
                  onClick={() => handleCompanySelected(company)}
                  key={company.t}
                  id={"COMPANY_SEARCH_" + idx}
                  sx={{
                    padding: "0.375rem 0.625rem",
                    background:
                      idx === activeIndex ? theme.brand.gray[500] : null,
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                  }}
                >
                  <Typography>{`${company.n} (${company.t})`}</Typography>
                </Box>
              );
            })}
          </Stack>
        )}

        {tickersLoading && (
          <Stack alignItems={"center"} marginTop={2} marginBottom={2}>
            <DefaultLoader size={48} />
            <Typography variant={"subtitle2"} color={"secondary"}>
              {"Loading ticker list"}
            </Typography>
          </Stack>
        )}

        {isLoading && (
          <Stack alignItems={"center"} marginTop={2} marginBottom={2}>
            <DefaultLoader size={48} />
            <Typography variant={"subtitle2"} color={"secondary"}>
              {"Fetching company details"}
            </Typography>
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export default CompanySearch;
